import React, { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { detectQuestionColumns } from "../agenda/detect";
import {
  saveAgendaDatabookPin,
  stashDatabookBlob,
  type AgendaDatabookPin,
} from "../agenda/storage";
import { apiFetch } from "../../../api/fddClient";
import { defaultColumnRoles, parsePeriodYM, type ColumnRole } from "./columnRoles";

type Props = {
  projectId: string;
  projectName?: string;
};

type AnalyzeAction = { label: string; action: string };

type SectionKey =
  | "current_assets"
  | "non_current_assets"
  | "current_liabilities"
  | "non_current_liabilities"
  | "equity";

const SECTION_LABELS: Record<SectionKey, string> = {
  current_assets: "Current Assets",
  non_current_assets: "Non-Current Assets",
  current_liabilities: "Current Liabilities",
  non_current_liabilities: "Non-Current Liabilities",
  equity: "Equity",
};

type UploadInfo = {
  sessionId: string;
  fileName: string;
  fileSize: number;
};

type AnalyzeInfo = {
  summary: string;
  actions: AnalyzeAction[];
  /** Distinct values found in the user-mapped entity column. */
  entities: string[];
  isAccountCount: number;
  bsAccountCount: number;
  /** Raw period labels (after first_period mapping). Drives the
   *  Consolidating period picker — FY labels are derived client-side. */
  periodLabels: string[];
};

/** Derive the list of available period labels from period headers + the
 *  user's fyEndMonth. Returns them in chronological order, oldest first.
 *  Mirrors the server's FY/LTM detection in builder.ts: if the latest
 *  period isn't an FY end AND we have ≥12 chronological months, the most
 *  recent label becomes "LTM <Mon>-<YY>" instead of "FY<YY>" — matching
 *  what the FY column header shows on the IS/BS Monthly sheets. */
function deriveFyLabels(periodLabels: string[], fyEndMonth: number): string[] {
  const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const parsed: { year: number; month: number }[] = [];
  for (const p of periodLabels) {
    const ym = parsePeriodYM(p);
    if (ym) parsed.push(ym);
  }
  if (parsed.length === 0) return [];
  parsed.sort((a, b) => (a.year - b.year) || (a.month - b.month));
  const fyOf = (y: number, m: number) => (m <= fyEndMonth ? y : y + 1);
  const fys = new Set<number>();
  for (const p of parsed) fys.add(fyOf(p.year, p.month));
  const sortedFys = [...fys].sort((a, b) => a - b);
  const last = parsed[parsed.length - 1];
  const isLtm = last.month !== fyEndMonth && parsed.length >= 12;
  return sortedFys.map((y, idx) => {
    const isLastFy = idx === sortedFys.length - 1;
    if (isLastFy && isLtm) {
      return `LTM ${MON[last.month - 1]}-${String(last.year).slice(-2)}`;
    }
    return `FY${String(y).slice(-2)}`;
  });
}

/** A single inconsistent GL — same number used with multiple "fingerprints"
 *  (combos of account name + mapping cols + IS/BS) across the TB's rows. The
 *  user picks one fingerprint per GL in the modal; that pick gets written back
 *  to every row of that GL during /analyze on the re-submit. */
type GlFingerprint = { values: string[]; rowCount: number };
type GlConflict = { glKey: string; columns: string[]; fingerprints: GlFingerprint[] };

/** Per-FY verdict from the cumulative-TB detector. `cumulative` is the
 *  recommendation; the user can flip it before clicking Convert. */
type CumulativeRun = {
  key: string;
  periodLabels: string[];
  monthCount: number;
  includesTrailing: boolean;
};

type RevenueTableRow = {
  periodIdx: number;
  periodLabel: string;
  summedRevenue: number;
  marker: "cumulating" | "reset" | "start";
};

type RevenueCandidate = { value: string; rowCount: number };

/** Rough per-account Claude cost in USD for each AI feature. Numbers are
 *  back-of-envelope (Sonnet 4 input $3/MTok + output $15/MTok). Used only
 *  for the "estimated cost" footer — not billed/exact. */
const AI_COST_PER_ACCOUNT = {
  clean_account_names: 0.0003,
  dso_dpo: 0.0002,
  sbs: 0.0003, // shared by bs_stratified, nwc, net_debt
  is_trending: 0.002,
  bs_trending: 0.002,
};

function estimateAICost(sel: DatabookSelection, isCount: number, bsCount: number): { low: number; high: number } {
  let base = 0;
  if (sel.clean_account_names) base += AI_COST_PER_ACCOUNT.clean_account_names * (isCount + bsCount);
  if (sel.dso_dpo) base += AI_COST_PER_ACCOUNT.dso_dpo * (isCount + bsCount);
  if (sel.bs_stratified || sel.nwc || sel.net_debt) base += AI_COST_PER_ACCOUNT.sbs * bsCount;
  if (sel.is_trending) base += AI_COST_PER_ACCOUNT.is_trending * isCount;
  if (sel.bs_trending) base += AI_COST_PER_ACCOUNT.bs_trending * bsCount;
  return { low: base * 0.7, high: base * 1.3 };
}

function formatCost(c: number): string {
  if (c < 0.01) return "<$0.01";
  if (c < 1) return `$${c.toFixed(2)}`;
  return `$${c.toFixed(2)}`;
}

/** Per-entity build config — one row per entity the user wants in the workbook. */
type EntityConfig = {
  rawName: string;
  displayName: string;
  includeIsBs: boolean;
  includeTrending: boolean;
  /** Whether this entity's TB rows feed into the Combined IS/BS sheets.
   *  Server defaults to true if undefined, so existing builds preserve
   *  behavior. */
  includeInCombined: boolean;
};

/** Preview returned by upload — first ~10 rows × ~12 cols for the mapping UI. */
type Preview = {
  headers: string[];
  rows: any[][];
  totalCols: number;
  totalRows: number;
};

const ROLE_OPTIONS: { value: ColumnRole; label: string }[] = [
  { value: "ignore", label: "(ignore)" },
  { value: "entity", label: "Entity" },
  { value: "account_number", label: "Account #" },
  { value: "account_name", label: "Account Name" },
  { value: "split", label: "IS / BS Split" },
  { value: "group1", label: "Grouping Level 1" },
  { value: "group2", label: "Grouping Level 2" },
  { value: "group3", label: "Grouping Level 3" },
  { value: "first_period", label: "First Period (and onward)" },
];

type Phase = "idle" | "uploading" | "mapping" | "analyzing" | "ready" | "building" | "done";

const ACCEPT = ".xlsx,.xls,.csv";

/**
 * Which sheets to build. Mirrors `DatabookSelection` on the server. Each key
 * maps 1:1 to a worksheet (or, for trending, to a Claude analysis pass that
 * also injects "Questions"/"Comments" columns onto the matching Monthly sheet).
 */
type DatabookSelection = {
  is_monthly: boolean;
  is_summary: boolean;
  is_trending: boolean;
  bs_monthly: boolean;
  bs_stratified: boolean;
  bs_summary: boolean;
  bs_trending: boolean;
  ebitda: boolean;
  nwc: boolean;
  net_debt: boolean;
  dso_dpo: boolean;
  clean_account_names: boolean;
  consolidating_is: boolean;
  consolidating_bs: boolean;
};

/** Default state — everything ON except the two slow trending Claude calls
 *  and bs_summary (parked, "coming soon"). */
const DEFAULT_SELECTION: DatabookSelection = {
  is_monthly: true,
  is_summary: true,
  is_trending: false,
  bs_monthly: true,
  bs_stratified: true,
  bs_summary: false,
  bs_trending: false,
  ebitda: true,
  nwc: true,
  net_debt: true,
  dso_dpo: true,
  clean_account_names: true,
  consolidating_is: false,
  consolidating_bs: false,
};

/** 1-based fiscal year-end month (1=Jan, 12=Dec). One number, no start month. */
type FyEndMonth = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

const FY_END_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

const FY_END_LS_KEY = (projectId: string) => `__databookFyEnd:${projectId}`;

function loadFyEnd(projectId: string): FyEndMonth {
  try {
    const raw = localStorage.getItem(FY_END_LS_KEY(projectId));
    const n = raw ? parseInt(raw, 10) : 12;
    if (n >= 1 && n <= 12) return n as FyEndMonth;
  } catch {}
  return 12;
}

function saveFyEnd(projectId: string, m: FyEndMonth) {
  try { localStorage.setItem(FY_END_LS_KEY(projectId), String(m)); } catch {}
}

type SelectionItem = {
  key: keyof DatabookSelection;
  label: string;
  slow?: boolean;
  /** Inline note shown below the label — kept concise; used for AI-powered items
   *  to tell the user what Claude is doing if this stays checked. */
  aiNote?: string;
  /** Renders the checkbox disabled with a "(coming soon)" tag and forces the
   *  selection off. Use for sheets that are intentionally parked. */
  comingSoon?: boolean;
};

const SELECTION_GROUPS: { title: string; items: SelectionItem[] }[] = [
  {
    title: "Income Statement",
    items: [
      { key: "is_monthly", label: "IS - Monthly" },
      { key: "is_summary", label: "IS - Summary" },
      {
        key: "is_trending",
        label: "IS Trending Analysis",
        slow: true,
        aiNote: "AI writes a trending question for each row, flagging anything worth asking management.",
      },
    ],
  },
  {
    title: "Balance Sheet",
    items: [
      { key: "bs_monthly", label: "BS - Monthly" },
      {
        key: "bs_stratified",
        label: "BS - Stratified",
        aiNote: "AI classifies every BS account into NWC / Net Debt / PP&E / Other.",
      },
      { key: "bs_summary", label: "BS - Summary", comingSoon: true },
      {
        key: "bs_trending",
        label: "BS Trending Analysis",
        slow: true,
        aiNote: "AI writes a trending question for each row, flagging anything worth asking management.",
      },
    ],
  },
  {
    title: "Standalone analyses",
    items: [
      { key: "ebitda", label: "Normalized EBITDA" },
      {
        key: "nwc",
        label: "NWC Schedule",
        aiNote: "AI classifies BS accounts into the NWC bucket and groups individual items by nature.",
      },
      {
        key: "net_debt",
        label: "Net Debt Schedule",
        aiNote: "AI classifies BS accounts as Net Debt and groups individual debt-like items by nature.",
      },
      {
        key: "dso_dpo",
        label: "DSO / DPO",
        aiNote: "AI classifies AR, AP, and the related revenue and expense captions to drive the DSO and DPO calc.",
      },
    ],
  },
  {
    title: "Cosmetic",
    items: [
      {
        key: "clean_account_names",
        label: "Clean account names",
        aiNote: "AI rewrites raw TB names into readable display labels.",
      },
    ],
  },
];

const DatabookPanel: React.FC<Props> = ({ projectId, projectName }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [upload, setUpload] = useState<UploadInfo | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzeInfo | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [selection, setSelection] = useState<DatabookSelection>(DEFAULT_SELECTION);
  const [fyEndMonth, setFyEndMonth] = useState<FyEndMonth>(() => loadFyEnd(projectId));
  // Units of the TB's amounts. "thousands" = already $'000, so the builder
  // doesn't divide by 1,000 again. Sent with the mapping, so rebuilds keep it.
  const [amountsIn, setAmountsIn] = useState<"dollars" | "thousands">("dollars");
  const [combinedLabel, setCombinedLabel] = useState<string>("Combined");
  const [entityConfigs, setEntityConfigs] = useState<EntityConfig[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [columnRoles, setColumnRoles] = useState<ColumnRole[]>([]);
  // Header-row pick + the first ~12 raw rows of the source sheet (no header
  // logic). Lets the user override the default "row 1 = header" when their
  // TB has title/summary rows above the actual header. Submitted to
  // /api/is-builder/repreview to re-extract.
  const [headerRow1Based, setHeaderRow1Based] = useState<number>(1);
  const [rawSample, setRawSample] = useState<string[][]>([]);
  // Period labels the user picked for Consolidating IS/BS sheets. Each label
  // matches a FY label produced by `deriveFyLabels`. Empty = nothing built.
  const [consolidatingPeriods, setConsolidatingPeriods] = useState<string[]>([]);

  // DSO/DPO uses both IS data (Sales / COGS) AND BS data (AR / AP). If the
  // user hasn't selected any IS or BS sheet, there's no meaningful output —
  // auto-uncheck and disable the box so it can't be shipped in a
  // contradictory state.
  const hasAnyISsheet = selection.is_monthly || selection.is_summary || selection.is_trending;
  const hasAnyBSsheet = selection.bs_monthly || selection.bs_stratified || selection.bs_summary || selection.bs_trending;
  const dsoDpoEligible = hasAnyISsheet && hasAnyBSsheet;
  useEffect(() => {
    if (!dsoDpoEligible && selection.dso_dpo) {
      setSelection((prev) => ({ ...prev, dso_dpo: false }));
    }
  }, [dsoDpoEligible, selection.dso_dpo]);

  const reset = useCallback(() => {
    setFile(null);
    setUpload(null);
    setAnalysis(null);
    setPreview(null);
    setColumnRoles([]);
    setEntityConfigs([]);
    setErrors([]);
    setRebuildOverrides(null);
    setRawSample([]);
    setHeaderRow1Based(1);
    setAmountsIn("dollars");
    setIsSectionCandidates([]);
    setIsSectionMapping({});
    setPhase("idle");
  }, []);

  /** Re-extract the TB with a new header-row pick (the user clicked a row in
   *  the raw-sheet sample table). Server re-opens the original file and
   *  overwrites the session's headers/rows so the column-mapping UI updates
   *  in place. */
  const runRepreview = useCallback(async (newHeaderRow: number) => {
    if (!upload) return;
    setErrors([]);
    try {
      const res = await apiFetch("/api/is-builder/repreview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: upload.sessionId, headerRow: newHeaderRow }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        pushError(err?.error || `Repreview failed (HTTP ${res.status})`);
        return;
      }
      const data = await res.json();
      const pv: Preview | null = data?.preview && Array.isArray(data.preview.headers) ? data.preview : null;
      if (!pv) {
        pushError("Repreview returned no preview.");
        return;
      }
      setPreview(pv);
      if (Array.isArray(data?.rawSample)) setRawSample(data.rawSample);
      if (Number.isFinite(Number(data?.headerRow1Based))) {
        setHeaderRow1Based(Number(data.headerRow1Based));
      }
      // Reset column roles to defaults — the column layout may have shifted
      // when the user picked a new header row.
      const guess: number = Number.isFinite(Number(data?.firstPeriodColGuess)) ? Number(data.firstPeriodColGuess) : -1;
      setColumnRoles(defaultColumnRoles(pv.headers, guess));
    } catch (err: any) {
      pushError(err?.message || "Repreview failed");
    }
  }, [upload]);

  const pushError = (msg: string) => setErrors((prev) => [...prev, msg]);

  const runUpload = useCallback(
    async (chosen: File) => {
      setErrors([]);
      setUpload(null);
      setAnalysis(null);
      setPreview(null);
      setColumnRoles([]);
      setPhase("uploading");

      const fd = new FormData();
      fd.append("files", chosen);
      if (projectName) fd.append("projectName", projectName);

      try {
        const res = await apiFetch("/api/is-builder/upload", { method: "POST", body: fd });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          pushError(err?.error || `Upload failed (HTTP ${res.status})`);
          setPhase("idle");
          return;
        }
        const data = await res.json();
        const sessionId = data.sessionId;
        setUpload({ sessionId, fileName: chosen.name, fileSize: chosen.size });

        // Rebuild detection: server detected this is a previously-built
        // databook (carries _DatabookMeta + Classifications). Skip the
        // mapping UI, fire analyze + generate inline with the embedded
        // mapping + user-edited classification overrides.
        if (data?.rebuild?.isRebuild && data.rebuild.mapping) {
          await runRebuildAuto(sessionId, data.rebuild);
          return;
        }

        // Capture the raw-sheet sample + the server's header-row pick so the
        // mapping UI can render a header-row override widget.
        if (Array.isArray(data?.rawSample)) setRawSample(data.rawSample);
        if (Number.isFinite(Number(data?.headerRow1Based))) {
          setHeaderRow1Based(Number(data.headerRow1Based));
        }
        // Surface non-fatal warnings (e.g., "no period columns detected with
        // header row N") so the user knows to pick a different row.
        if (Array.isArray(data?.warnings)) {
          for (const w of data.warnings) pushError(String(w));
        }

        const pv: Preview | null = data?.preview && Array.isArray(data.preview.headers) ? data.preview : null;
        if (pv) {
          setPreview(pv);
          // Pre-fill roles from the header text, falling back to the FDD
          // template's positional layout (plus the server's first-period
          // guess) when the headers aren't recognisable. User can adjust
          // before applying.
          const guess: number = Number.isFinite(Number(data?.firstPeriodColGuess)) ? Number(data.firstPeriodColGuess) : -1;
          setColumnRoles(defaultColumnRoles(pv.headers, guess));
          setPhase("mapping");
        } else {
          setPhase("idle");
          pushError("Upload succeeded but no preview returned — cannot map columns.");
        }
      } catch (err: any) {
        pushError(err?.message || "Upload failed");
        setPhase("idle");
      }
    },
    [projectName],
  );

  // Stashed when /upload detects a rebuild. Used by handleCreateDatabook to
  // pass classificationOverrides on the next generate call. Cleared after
  // the build completes or the user resets.
  const [rebuildOverrides, setRebuildOverrides] = useState<any | null>(null);

  // ── Section-resolver state ──
  // Surfaced when the server returns 422 with error="section_detection_failed".
  // The user picks which of their top-level BS Mapping-1 nodes corresponds to
  // each missing canonical section, and we re-call the build with the picks
  // as `sectionOverrides` in the payload.
  const [sectionPrompt, setSectionPrompt] = useState<{
    missing: SectionKey[];
    candidates: string[];
    allBsNodes: string[];
    matched: Partial<Record<SectionKey, string>>;
  } | null>(null);
  const [sectionPicks, setSectionPicks] = useState<Partial<Record<SectionKey, string>>>({});

  // ── GL # consistency modal state ──
  // Populated when /analyze returns one or more GL conflicts. The user picks
  // a canonical fingerprint per GL; on Continue we re-fire /analyze with
  // glResolutions and proceed to the ready phase as if the conflicts never
  // existed.
  const [glConflictState, setGlConflictState] = useState<{
    conflicts: GlConflict[];
    mapping: any;
    picks: Record<string, number>;
  } | null>(null);

  // Current build progress label (e.g. "Generating BS Trending"). Surfaced
  // next to the Create Databook button while the job is running so the user
  // can see something is happening.
  const [buildProgress, setBuildProgress] = useState<string>("");
  const [buildWarnings, setBuildWarnings] = useState<string[]>([]);

  // ── Cumulative-runs modal state ──
  // Set when /generate returns 422 `cumulative_runs_detected`. Lists the
  // chronological runs the detector found, with checkboxes auto-selected.
  // The summed-revenue table is shown below so the user can eyeball the
  // shape. On submit we re-fire /generate with the picks baked in.
  const [cumulativePrompt, setCumulativePrompt] = useState<{
    runs: CumulativeRun[];
    table: RevenueTableRow[];
    picks: Record<string, boolean>;
    revenueOverride?: string;
  } | null>(null);

  // ── Revenue-picker modal state ──
  // Set when /generate returns 422 `revenue_section_unidentified` because
  // the cumulative detector couldn't auto-locate revenue rows. The user
  // picks the mapping label; we re-fire /generate with that override.
  const [revenuePicker, setRevenuePicker] = useState<{
    candidates: RevenueCandidate[];
    pick?: string;
  } | null>(null);

  // ── IS section classification state ──
  // Populated after /analyze returns isSectionCandidates. The user can
  // confirm or override each L1 section's category before building.
  const [isSectionCandidates, setIsSectionCandidates] = useState<{ label: string; category: string }[]>([]);
  const [isSectionMapping, setIsSectionMapping] = useState<Record<string, string>>({});

  // ── Rebuild flow ──
  // When the user re-uploads a previously-built databook, the server returns
  // the embedded mapping + classification overrides in the upload response.
  // We auto-fire /chat?action=analyze here (the mapping was captured at the
  // original build, so there's nothing for the user to re-confirm), then
  // land on the "ready" phase WITH the previous selection pre-filled so the
  // user can decide what to include this time (e.g. add trending). The build
  // happens when they click the existing "Create databook" button — the
  // classificationOverrides are picked up automatically via rebuildOverrides.
  const runRebuildAuto = async (sessionId: string, rebuild: any, glResolutions?: Record<string, number>) => {
    setPhase("analyzing");
    try {
      const aRes = await apiFetch("/api/is-builder/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId,
          action: "analyze",
          projectName: rebuild.projectName || projectName,
          mapping: rebuild.mapping,
          glResolutions,
        }),
      });
      if (!aRes.ok) {
        const err = await aRes.json().catch(() => ({}));
        pushError(err?.error || `Rebuild analyze failed (HTTP ${aRes.status})`);
        setPhase("idle");
        return;
      }
      const aData = await aRes.json();
      // Surface any GL conflicts even on rebuild — the user may have edited
      // the TB inside their previous output databook (added rows, renamed
      // accounts, fixed a mapping on some rows but not others). Same modal,
      // same retry-with-resolutions flow.
      const conflicts: GlConflict[] = Array.isArray(aData?.glConflicts) ? aData.glConflicts : [];
      if (conflicts.length > 0 && !glResolutions) {
        setGlConflictState({ conflicts, mapping: rebuild.mapping, picks: {} });
        // Stash the rebuild context so once the modal resolves we can finish
        // restoring selection / fyEndMonth / classification overrides.
        setPendingRebuild(rebuild);
        setPhase("mapping");
        return;
      }
      const summary = aData?.messages?.[0]?.content || "";
      const actions: AnalyzeAction[] = aData?.messages?.[0]?.actions || [];
      const entities: string[] = Array.isArray(aData?.entities) ? aData.entities : [];
      const isAccountCount: number = Number(aData?.isAccountCount) || 0;
      const bsAccountCount: number = Number(aData?.bsAccountCount) || 0;
      const periodLabels: string[] = Array.isArray(aData?.periodLabels) ? aData.periodLabels : [];
      setAnalysis({ summary, actions, entities, isAccountCount, bsAccountCount, periodLabels });
      setEntityConfigs(entities.map((rawName: string) => ({
        rawName,
        displayName: rawName,
        includeIsBs: false,
        includeTrending: false,
        includeInCombined: true,
      })));
      const rebuildCandidates = Array.isArray(aData?.isSectionCandidates) ? aData.isSectionCandidates as { label: string; category: string }[] : [];
      setIsSectionCandidates(rebuildCandidates);
      const rebuildGuessed: Record<string, string> = {};
      for (const c of rebuildCandidates) rebuildGuessed[c.label] = c.category;
      setIsSectionMapping(rebuildGuessed);
      // Pre-fill selection + fyEndMonth from the prior build so this rebuild
      // matches by default. User can flip anything before clicking "Create
      // databook" (e.g., turn on trending now).
      if (rebuild.selection) setSelection({ ...DEFAULT_SELECTION, ...rebuild.selection });
      if (typeof rebuild.fyEndMonth === "number") setFyEndMonth(rebuild.fyEndMonth as FyEndMonth);
      setRebuildOverrides(rebuild.classificationOverrides ?? null);
      setPhase("ready");
    } catch (err: any) {
      pushError(err?.message || "Rebuild analyze failed");
      setPhase("idle");
    }
  };

  // Pending rebuild context — stashed when the GL conflict modal pops during
  // a rebuild. Once the user resolves, runRebuildAuto re-fires with the
  // resolutions and uses this context to restore selection + overrides.
  const [pendingRebuild, setPendingRebuild] = useState<any | null>(null);

  const applyMapping = useCallback(async () => {
    if (!upload || !preview) return;
    setErrors([]);
    setPhase("analyzing");

    // Translate per-column ColumnRole[] into the server's mapping shape.
    const firstPeriodIdx = columnRoles.findIndex((r) => r === "first_period");
    if (firstPeriodIdx < 0) {
      pushError('Pick a column to mark as "First Period (and onward)".');
      setPhase("mapping");
      return;
    }
    const findRole = (r: ColumnRole) => columnRoles.findIndex((cr) => cr === r);
    const entityCol = findRole("entity");
    const accountNameCol = findRole("account_name");
    const accountNumberCol = findRole("account_number");
    const splitCol = findRole("split");
    const groupingLevels: { col: number; level: number; name: string }[] = [];
    for (const [role, level] of [["group1", 1], ["group2", 2], ["group3", 3]] as const) {
      const idx = findRole(role);
      if (idx >= 0) groupingLevels.push({ col: idx, level, name: preview.headers[idx] || "" });
    }
    if (accountNameCol < 0) {
      pushError('Pick a column to mark as "Account Name".');
      setPhase("mapping");
      return;
    }

    // splitValues are computed server-side from the full TB (the preview is
    // too small a sample — early rows can be all BS, missing IS values).
    const mapping = {
      entityCol: entityCol >= 0 ? entityCol : null,
      accountNameCol,
      accountNumberCols: accountNumberCol >= 0 ? [accountNumberCol] : [],
      splitCol: splitCol >= 0 ? splitCol : null,
      groupingLevels,
      firstPeriodCol: firstPeriodIdx,
      amountsIn,
    };

    await runAnalyze(mapping, undefined);
  }, [upload, preview, columnRoles, projectName, amountsIn]);

  /** Re-usable analyze caller. Pass `glResolutions` on the second call after
   *  the conflict modal collects picks; pass `undefined` for the first call.
   *  Pulled out of applyMapping so the modal's Continue handler can re-fire
   *  /analyze with the same mapping plus resolutions. */
  const runAnalyze = async (mapping: any, glResolutions: Record<string, number> | undefined): Promise<void> => {
    if (!upload) return;
    try {
      const res = await apiFetch("/api/is-builder/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: upload.sessionId,
          action: "analyze",
          projectName,
          mapping,
          glResolutions,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        pushError(err?.error || `Analysis failed (HTTP ${res.status})`);
        setPhase("mapping");
        return;
      }
      const data = await res.json();
      // Surface GL # conflicts via the modal BEFORE landing on the ready
      // phase. The user resolves and re-fires the same analyze with the
      // resolutions baked in.
      const conflicts: GlConflict[] = Array.isArray(data?.glConflicts) ? data.glConflicts : [];
      if (conflicts.length > 0 && !glResolutions) {
        setGlConflictState({ conflicts, mapping, picks: {} });
        setPhase("mapping");
        return;
      }
      const summary = data?.messages?.[0]?.content || "";
      const actions: AnalyzeAction[] = data?.messages?.[0]?.actions || [];
      const entities: string[] = Array.isArray(data?.entities) ? data.entities : [];
      const isAccountCount: number = Number(data?.isAccountCount) || 0;
      const bsAccountCount: number = Number(data?.bsAccountCount) || 0;
      const periodLabels: string[] = Array.isArray(data?.periodLabels) ? data.periodLabels : [];
      setAnalysis({ summary, actions, entities, isAccountCount, bsAccountCount, periodLabels });
      // Per-entity tabs default OFF when there are multiple entities — most
      // builds only need the Combined view, so opt-in per entity.
      setEntityConfigs(entities.map((rawName) => ({
        rawName,
        displayName: rawName,
        includeIsBs: false,
        includeTrending: false,
        includeInCombined: true,
      })));
      const candidates = Array.isArray(data?.isSectionCandidates) ? data.isSectionCandidates as { label: string; category: string }[] : [];
      setIsSectionCandidates(candidates);
      const guessed: Record<string, string> = {};
      for (const c of candidates) guessed[c.label] = c.category;
      setIsSectionMapping(guessed);
      setPhase("ready");
    } catch (err: any) {
      pushError(err?.message || "Analysis failed");
      setPhase("mapping");
    }
  };

  const handlePickFile = (chosen: File | undefined) => {
    if (!chosen) return;
    setFile(chosen);
    void runUpload(chosen);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    handlePickFile(Array.from(e.dataTransfer?.files || [])[0]);
  };

  const buildAction = (analysis?.actions || []).find((a) => a.action === "generate_both")
    ?? (analysis?.actions || []).find((a) => a.action === "generate")
    ?? (analysis?.actions || []).find((a) => a.action === "generate_bs");

  const runBuild = async (
    sectionOverrides?: Partial<Record<SectionKey, string>>,
    cumulativeFix?: Record<string, boolean>,
    revenueOverride?: string,
  ) => {
    if (!upload || !buildAction) return;
    setErrors([]);
    setPhase("building");
    try {
      const res = await apiFetch("/api/is-builder/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: upload.sessionId,
          action: buildAction.action,
          projectName,
          projectId, // canonical-store key — server saves the build under this id
          selection,
          fyEndMonth,
          combinedLabel,
          // Effective entity configs: when Consolidating is on, force
          // includeIsBs=true for every entity included in Combined so the
          // per-entity IS/BS sheets exist for the Consolidating cross-sheet
          // refs to resolve. UI shows the locked-checkbox state to match.
          entityConfigs: entityConfigs.map((ec) =>
            (selection.consolidating_is || selection.consolidating_bs) && ec.includeInCombined
              ? { ...ec, includeIsBs: true }
              : ec,
          ),
          // Present only on a rebuild — server's classifiers skip Claude for
          // any GL the user has already classified on the Classifications
          // sheet of the re-uploaded databook.
          classificationOverrides: rebuildOverrides ?? undefined,
          // Present only after the user resolved a section-detection prompt.
          // Maps canonical section key → top-level BS node name they picked.
          sectionOverrides,
          // Present only after the user resolved the cumulative-runs modal.
          // Keyed by run key ("<firstIdx>:<lastIdx>") → true to decumulate.
          cumulativeFix,
          // Present only after the user picked a revenue label in the
          // revenue picker modal. Tells the server which mapping value to
          // treat as revenue for cumulative detection.
          revenueOverride,
          // IS section classification confirmed by the user (or auto-guessed).
          // Maps each L1 section name to Revenue / COGS / OPEX / Below EBITDA.
          isSectionMapping,
          // Period labels for Consolidating IS/BS sheets. Server ignores
          // this when both consolidating selections are false. Filtered
          // against the current FY picker options so stale labels from a
          // previous analysis (e.g. "FY26" left over after the period shifted
          // to "LTM Apr-26") don't reach the server and trigger a duplicate
          // sheet-name collision.
          consolidatingPeriods: Array.from(new Set(
            consolidatingPeriods.filter((p) => deriveFyLabels(analysis?.periodLabels || [], fyEndMonth).includes(p))
          )),
        }),
      });
      if (res.status === 422) {
        const err = await res.json().catch(() => ({}));
        if (err?.error === "section_detection_failed" && Array.isArray(err.missing)) {
          setSectionPrompt({
            missing: err.missing as SectionKey[],
            candidates: (err.candidates as string[]) || [],
            allBsNodes: (err.allBsNodes as string[]) || [],
            matched: (err.matched as Partial<Record<SectionKey, string>>) || {},
          });
          setSectionPicks({});
          setPhase("ready");
          return;
        }
        if (err?.error === "cumulative_runs_detected" && Array.isArray(err.runs)) {
          // Every detected run is auto-selected — user can untick anything
          // that looks wrong before submitting.
          const picks: Record<string, boolean> = {};
          for (const r of err.runs as CumulativeRun[]) picks[r.key] = true;
          setCumulativePrompt({
            runs: err.runs as CumulativeRun[],
            table: (err.table as RevenueTableRow[]) || [],
            picks,
            revenueOverride,
          });
          setPhase("ready");
          return;
        }
        if (err?.error === "revenue_section_unidentified" && Array.isArray(err.candidates)) {
          setRevenuePicker({
            candidates: err.candidates as RevenueCandidate[],
            pick: undefined,
          });
          setPhase("ready");
          return;
        }
        if (err?.error === "no_entities_selected_for_combined") {
          pushError("At least one entity must be checked under \"In Combined\" — the Combined IS/BS aggregates only the entities you select.");
          setPhase("ready");
          return;
        }
        pushError(err?.error || `Build failed (HTTP 422)`);
        setPhase("ready");
        return;
      }
      if (!res.ok && res.status !== 202) {
        const err = await res.json().catch(() => ({}));
        pushError(err?.error || `Build failed (HTTP ${res.status})`);
        setPhase("ready");
        return;
      }
      // Job-queue pattern: /generate returns 202 { jobId }; we poll until the
      // build completes, then fetch the .xlsx blob via /jobs/:id/result. This
      // keeps each HTTP request short so the React dev-server proxy doesn't
      // kill long-running builds mid-flight.
      const startJson = await res.json().catch(() => ({}));
      const jobId: string | undefined = startJson?.jobId;
      if (!jobId) {
        pushError("Build started but no job ID returned.");
        setPhase("ready");
        return;
      }
      // Poll for status. Backoff: 1s, then 3s steady. No timeout — let the
      // server take however long it needs.
      let attempts = 0;
      // Show the current step in the build progress label.
      let lastProgress = "Starting…";
      setBuildProgress(lastProgress);
      setBuildWarnings([]);
      let lastWarnings: string[] = [];
      while (true) {
        attempts++;
        await new Promise((r) => setTimeout(r, attempts < 3 ? 1000 : 3000));
        const sRes = await apiFetch(`/api/is-builder/jobs/${jobId}`).catch(() => null);
        if (!sRes || !sRes.ok) continue;
        const sJson = await sRes.json().catch(() => null);
        if (!sJson) continue;
        if (sJson.progress && sJson.progress !== lastProgress) {
          lastProgress = sJson.progress;
          setBuildProgress(lastProgress);
        }
        if (Array.isArray(sJson.warnings)) lastWarnings = sJson.warnings;
        if (sJson.status === "error") {
          pushError(sJson.error || "Build failed.");
          setBuildProgress("");
          setBuildWarnings(lastWarnings);
          setPhase("ready");
          return;
        }
        if (sJson.status === "done") {
          setBuildWarnings(lastWarnings);
          break;
        }
      }
      // Job done — fetch the blob.
      setBuildProgress("Downloading…");
      const blobRes = await apiFetch(`/api/is-builder/jobs/${jobId}/result`);
      if (!blobRes.ok) {
        pushError(`Result download failed (HTTP ${blobRes.status})`);
        setBuildProgress("");
        setPhase("ready");
        return;
      }
      const blob = await blobRes.blob();
      setBuildProgress("");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cleanName = upload.fileName.replace(/\.(xlsx|xls|csv)$/i, "");
      const downloadName = `${cleanName} - Databook.xlsx`;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Auto-pin the databook for the Agenda Builder. Run async so it
      // doesn't block the "done" phase or surface a failure to the user —
      // worst case the agenda panel just doesn't auto-populate.
      // Skip when no monthly sheet was selected — the agenda detector keys
      // off IS - Monthly / BS - Monthly question columns, so without them
      // there's nothing for it to pin.
      if (selection.is_monthly || selection.bs_monthly) {
        void autoPinForAgenda(projectId, blob, downloadName).catch((err) =>
          console.warn("[agenda] auto-pin after databook build failed:", err),
        );
      }

      // Tell the global "Saved databook" indicator (and anything else that
      // cares) that this project's canonical store just got refreshed.
      // The server has already persisted the artifacts by the time we get
      // here — the response was sent after saveBuild() completed.
      window.dispatchEvent(new CustomEvent("databook-saved", { detail: { projectId } }));

      setPhase("done");
    } catch (err: any) {
      pushError(err?.message || "Build failed");
      setPhase("ready");
    }
  };

  const handleCreateDatabook = () => { void runBuild(); };

  const handleSectionPromptSubmit = () => {
    if (!sectionPrompt) return;
    const overrides: Partial<Record<SectionKey, string>> = { ...sectionPrompt.matched };
    let allFilled = true;
    for (const key of sectionPrompt.missing) {
      const pick = sectionPicks[key];
      if (!pick) { allFilled = false; break; }
      overrides[key] = pick;
    }
    if (!allFilled) {
      pushError("Pick a value for each missing section before continuing.");
      return;
    }
    setSectionPrompt(null);
    void runBuild(overrides);
  };

  /**
   * Stash the just-built databook bytes in IndexedDB and write a pin
   * metadata entry pointing at it. Detection runs synchronously here
   * because the workbook is already in memory — no network involved.
   */
  async function autoPinForAgenda(projectId: string, blob: Blob, fileName: string): Promise<void> {
    const buf = await blob.arrayBuffer();
    const workbook = XLSX.read(buf, { type: "array" });
    const detection = detectQuestionColumns(workbook);

    const questionColByTab: Record<string, number | null> = {};
    const agendaColByTab: Record<string, number | null> = {};
    const irlColByTab: Record<string, number | null> = {};
    const headerRowByTab: Record<string, number> = {};
    const sheetNameByTab: Record<string, string> = {};
    for (const t of detection.tabs) {
      questionColByTab[t.tab] = t.questionCol;
      agendaColByTab[t.tab] = t.agendaCol ?? null;
      irlColByTab[t.tab] = t.irlCol ?? null;
      headerRowByTab[t.tab] = t.headerRow;
      if (t.actualSheetName) sheetNameByTab[t.tab] = t.actualSheetName;
    }

    const pin: AgendaDatabookPin = {
      source: "builder",
      fileName,
      pinnedAt: new Date().toISOString(),
      fileSize: blob.size,
      questionColByTab,
      agendaColByTab,
      irlColByTab,
      headerRowByTab,
      sheetNameByTab,
      detectionConfidence: detection.overallConfidence,
    };

    // Stash the bytes first — if that fails, don't write a stale pin.
    await stashDatabookBlob(projectId, buf);
    saveAgendaDatabookPin(projectId, pin);
    window.dispatchEvent(new CustomEvent("agenda-pin-updated", { detail: { projectId } }));
  }

  const busy = phase === "uploading" || phase === "analyzing" || phase === "building";

  return (
    <section
      style={{
        border: "1px solid var(--theme-border)",
        borderRadius: 10,
        background: "var(--theme-tint-3)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 13 }}>
        Databook Builder
      </div>

      {/* File input — hidden once a file has been picked */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          style={{
            padding: 24,
            border: `2px dashed ${dragOver ? "var(--theme-action)" : "var(--theme-accent)"}`,
            background: dragOver ? "var(--theme-tint-21)" : "var(--theme-tint-7)",
            color: "var(--theme-ink)",
            borderRadius: 10,
            textAlign: "center",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          <label style={{ cursor: "pointer", display: "block" }}>
            <input
              type="file"
              accept={ACCEPT}
              onChange={(e) => handlePickFile(e.target.files?.[0])}
              style={{ display: "none" }}
            />
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Drop a trial balance here, or click to choose
            </div>
            <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>
              Accepts .xlsx, .xls, .csv. One file at a time.
            </div>
          </label>
        </div>
      )}

      {/* Loaded file row */}
      {file && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            background: "#ffffff",
            border: "1px solid var(--theme-border)",
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <span style={{ flex: 1, fontFamily: "monospace", color: "#333" }}>
            {file.name}
          </span>
          <span style={{ color: "#666", fontSize: 12 }}>
            {(file.size / 1024).toFixed(0)} KB
          </span>
          <button
            onClick={reset}
            disabled={busy}
            style={{
              background: "transparent",
              color: "#dc2626",
              border: "1px solid #fca5a5",
              borderRadius: 6,
              padding: "2px 10px",
              cursor: busy ? "not-allowed" : "pointer",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            Replace
          </button>
        </div>
      )}

      {/* Upload + analyze status */}
      {(phase === "uploading" || phase === "analyzing") && (
        <div style={{ fontSize: 12, color: "var(--theme-ink-soft)", fontStyle: "italic" }}>
          {phase === "uploading" ? "Reading trial balance…" : "Applying mapping…"}
        </div>
      )}

      {/* Column-mapping UI — shown once the upload returns a preview. */}
      {phase === "mapping" && preview && (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid var(--theme-border)",
            borderRadius: 8,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={() => setColumnRoles((prev) => {
                  // Shift all role assignments LEFT by one column. Useful when
                  // the TB has an extra leading column the defaults didn't
                  // anticipate — slide everything back instead of clicking
                  // through every dropdown.
                  const len = preview?.headers?.length ?? prev.length;
                  const next: ColumnRole[] = new Array(len).fill("ignore");
                  for (let i = 0; i < len - 1; i++) next[i] = prev[i + 1] || "ignore";
                  return next;
                })}
                title="Shift all column assignments one column left"
                style={{ fontSize: 11, padding: "3px 8px", border: "1px solid var(--theme-border)", background: "var(--theme-tint-7)", borderRadius: 4, cursor: "pointer" }}
              >
                ← Shift left
              </button>
              <button
                type="button"
                onClick={() => setColumnRoles((prev) => {
                  // Shift all role assignments RIGHT by one column.
                  const len = preview?.headers?.length ?? prev.length;
                  const next: ColumnRole[] = new Array(len).fill("ignore");
                  for (let i = 1; i < len; i++) next[i] = prev[i - 1] || "ignore";
                  return next;
                })}
                title="Shift all column assignments one column right"
                style={{ fontSize: 11, padding: "3px 8px", border: "1px solid var(--theme-border)", background: "var(--theme-tint-7)", borderRadius: 4, cursor: "pointer" }}
              >
                Shift right →
              </button>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--theme-ink)" }}>Map columns</div>
          </div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            Tell the builder which column is which. Defaults assume the standard FDD template
            (Entity, Account #, Account Name, IS/BS, Category, Mapping 2, Mapping 3, then periods).
            Use the shift buttons if your TB has an extra leading or trailing column.
          </div>

          {/* Header-row picker. Shown only when the server returned a raw
              sample. The user clicks the row that contains the actual column
              headers (e.g. row 6 when there's a title block above). The
              server re-extracts and the preview below this panel updates. */}
          {rawSample.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px", background: "#fffdf4", border: "1px solid #f1e6b3", borderRadius: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#7c5e10" }}>Header row</div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                Click the row containing the column headers. Currently using <strong>row {headerRow1Based}</strong>.
                Skip this if row 1 is already the header.
              </div>
              <div style={{ overflowX: "auto", border: "1px solid #f1e6b3", borderRadius: 4, maxHeight: 220 }}>
                <table style={{ borderCollapse: "collapse", fontSize: 10, width: "max-content" }}>
                  <tbody>
                    {rawSample.map((row, ri) => {
                      const rowNum1 = ri + 1;
                      const isChosen = rowNum1 === headerRow1Based;
                      return (
                        <tr
                          key={ri}
                          onClick={() => {
                            if (rowNum1 !== headerRow1Based) void runRepreview(rowNum1);
                          }}
                          style={{ cursor: "pointer", background: isChosen ? "#fff3a0" : (ri % 2 === 0 ? "white" : "#fafaf2") }}
                          title={isChosen ? "Currently selected as header row" : `Click to use row ${rowNum1} as the header`}
                        >
                          <td style={{ padding: "2px 6px", borderRight: "1px solid #f1e6b3", color: "#7c5e10", fontWeight: 700, position: "sticky", left: 0, background: isChosen ? "#fff3a0" : "#fffdf4" }}>
                            {rowNum1}
                          </td>
                          {row.map((cell, ci) => (
                            <td key={ci} style={{ padding: "2px 6px", color: "#374151", whiteSpace: "nowrap", borderRight: "1px solid #f5f5f5", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis" }}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, width: "max-content" }}>
              <thead>
                <tr>
                  {preview.headers.map((_h, c) => (
                    <th key={c} style={{ borderBottom: "1px solid #e5e7eb", padding: 4, background: "#f9fafb", minWidth: 130 }}>
                      <select
                        value={columnRoles[c] || "ignore"}
                        onChange={(e) => setColumnRoles((prev) => {
                          const next = [...prev];
                          next[c] = e.target.value as ColumnRole;
                          return next;
                        })}
                        style={{ width: "100%", fontSize: 11, padding: "2px 4px", borderRadius: 4, border: "1px solid var(--theme-border)" }}
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </th>
                  ))}
                </tr>
                <tr>
                  {preview.headers.map((h, c) => (
                    <th key={c} style={{ borderBottom: "1px solid var(--theme-border)", padding: "4px 6px", fontWeight: 700, color: "var(--theme-ink)", textAlign: "left", background: "var(--theme-tint-7)" }}>
                      {h || `Col ${c + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row, ri) => (
                  <tr key={ri}>
                    {preview.headers.map((_h, c) => (
                      <td key={c} style={{ borderBottom: "1px solid #f1f5f9", padding: "3px 6px", color: "#374151", whiteSpace: "nowrap" }}>
                        {row[c] == null ? "" : String(row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 10, color: "#9ca3af" }}>
            Showing first {preview.rows.length} of {preview.totalRows} rows · {preview.headers.length} of {preview.totalCols} columns.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="databook-amounts-in" style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>TB amounts are in:</label>
            <select
              id="databook-amounts-in"
              value={amountsIn}
              onChange={(e) => setAmountsIn(e.target.value === "thousands" ? "thousands" : "dollars")}
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid var(--theme-border)",
                fontSize: 12,
                background: "#fff",
              }}
            >
              <option value="dollars">Dollars (divide by 1,000)</option>
              <option value="thousands">Thousands (use as they are)</option>
            </select>
            <span style={{ fontSize: 11, color: "#6b7280" }}>The databook shows $'000 either way.</span>
          </div>
          <button
            onClick={() => void applyMapping()}
            disabled={busy}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "none",
              background: busy ? "#9ca3af" : "var(--theme-accent)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 13,
              cursor: busy ? "not-allowed" : "pointer",
              alignSelf: "flex-start",
            }}
          >
            Apply mapping
          </button>
        </div>
      )}

      {/* What-to-include picker — only shown once analysis is done. */}
      {analysis && upload && (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid var(--theme-border)",
            borderRadius: 8,
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {/* Fiscal-year setup — global to both Combined and Per-entity sheets. */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 4, borderBottom: "1px dashed var(--theme-border)" }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Fiscal year ends in:</label>
            <select
              value={fyEndMonth}
              onChange={(e) => {
                const m = parseInt(e.target.value, 10) as FyEndMonth;
                setFyEndMonth(m);
                saveFyEnd(projectId, m);
              }}
              style={{
                padding: "4px 8px",
                borderRadius: 6,
                border: "1px solid var(--theme-border)",
                fontSize: 12,
                background: "#fff",
              }}
            >
              {FY_END_MONTH_NAMES.map((name, idx) => (
                <option key={idx + 1} value={idx + 1}>{name}</option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
              If the TB ends mid-FY, an LTM column will be added.
            </span>
          </div>

          {/* Combined section — the entity-agnostic view (current behavior). */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8, borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--theme-ink)" }}>Combined</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 11, color: "var(--theme-ink-soft)", fontWeight: 600 }}>Display name:</label>
              <input
                type="text"
                value={combinedLabel}
                onChange={(e) => setCombinedLabel(e.target.value)}
                placeholder="Combined"
                style={{
                  flex: 1,
                  padding: "4px 8px",
                  borderRadius: 6,
                  border: "1px solid var(--theme-border)",
                  fontSize: 12,
                }}
              />
            </div>
            {SELECTION_GROUPS.map((group) => (
              <div key={group.title}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--theme-ink-soft)", marginBottom: 4 }}>
                  {group.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {group.items.map((item) => {
                    const isDsoDpoDisabled = item.key === "dso_dpo" && !dsoDpoEligible;
                    const isDisabled = isDsoDpoDisabled || !!item.comingSoon;
                    return (
                      <div
                        key={item.key}
                        style={{ display: "flex", flexDirection: "column", gap: 1 }}
                      >
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: 12,
                            color: isDisabled ? "#9ca3af" : "#1f2937",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={item.comingSoon ? false : selection[item.key]}
                            disabled={isDisabled}
                            onChange={(e) => setSelection((prev) => ({ ...prev, [item.key]: e.target.checked }))}
                            style={{ marginRight: 6 }}
                          />
                          {item.label}
                          {item.aiNote && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 9,
                                fontWeight: 700,
                                background: "#eef2ff",
                                color: "#4338ca",
                                padding: "1px 5px",
                                borderRadius: 4,
                                letterSpacing: 0.3,
                              }}
                            >
                              AI
                            </span>
                          )}
                          {item.slow && (
                            <span style={{ marginLeft: 6, fontSize: 10, color: "#9a3412", fontStyle: "italic" }}>
                              (slow)
                            </span>
                          )}
                          {item.comingSoon && (
                            <span style={{ marginLeft: 6, fontSize: 10, color: "#6b7280", fontStyle: "italic" }}>
                              (coming soon)
                            </span>
                          )}
                          {isDsoDpoDisabled && (
                            <span style={{ marginLeft: 6, fontSize: 10, color: "#6b7280", fontStyle: "italic" }}>
                              (needs an IS sheet + a BS sheet)
                            </span>
                          )}
                        </label>
                        {item.aiNote && (
                          <div style={{ fontSize: 10, color: "#6b7280", marginLeft: 22, fontStyle: "italic", lineHeight: 1.3 }}>
                            {item.aiNote}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Per-entity section — one row per detected entity. Hidden for single-entity TBs. */}
          {analysis.entities.length > 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--theme-ink)" }}>Per entity</div>
              <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
                Detected {analysis.entities.length} entities in column 1. Rename, deselect, or toggle trending per entity.
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 110px 110px", gap: 6, alignItems: "center", fontSize: 11 }}>
                <div style={{ fontWeight: 600, color: "var(--theme-ink-soft)" }}>Display name</div>
                <div style={{ fontWeight: 600, color: "var(--theme-ink-soft)", textAlign: "center" }}>In Combined</div>
                <div style={{ fontWeight: 600, color: "var(--theme-ink-soft)", textAlign: "center" }}>IS + BS</div>
                <div style={{ fontWeight: 600, color: "var(--theme-ink-soft)", textAlign: "center" }}>
                  Trending
                  <span style={{ marginLeft: 4, color: "#9a3412", fontStyle: "italic" }}>(slow)</span>
                </div>
                {entityConfigs.map((ec, idx) => (
                  <React.Fragment key={ec.rawName}>
                    <input
                      type="text"
                      value={ec.displayName}
                      placeholder={ec.rawName}
                      onChange={(e) => setEntityConfigs((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], displayName: e.target.value };
                        return next;
                      })}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        border: "1px solid var(--theme-border)",
                        fontSize: 12,
                      }}
                    />
                    <input
                      type="checkbox"
                      checked={ec.includeInCombined}
                      onChange={(e) => setEntityConfigs((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], includeInCombined: e.target.checked };
                        return next;
                      })}
                      style={{ justifySelf: "center" }}
                    />
                    <input
                      type="checkbox"
                      checked={ec.includeIsBs || ((selection.consolidating_is || selection.consolidating_bs) && ec.includeInCombined)}
                      disabled={(selection.consolidating_is || selection.consolidating_bs) && ec.includeInCombined}
                      title={(selection.consolidating_is || selection.consolidating_bs) && ec.includeInCombined ? "Locked on while Consolidating is enabled" : undefined}
                      onChange={(e) => setEntityConfigs((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], includeIsBs: e.target.checked };
                        return next;
                      })}
                      style={{ justifySelf: "center" }}
                    />
                    <input
                      type="checkbox"
                      checked={ec.includeTrending}
                      onChange={(e) => setEntityConfigs((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], includeTrending: e.target.checked };
                        return next;
                      })}
                      style={{ justifySelf: "center" }}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Consolidating statements section — only meaningful for multi-entity TBs.
              Lets the user generate one "Cons. IS" / "Cons. BS" sheet per selected
              period, showing each entity as its own column with a far-right total
              that ties back to Combined. */}
          {analysis.entities.length > 1 && (() => {
            const fyOptions = deriveFyLabels(analysis.periodLabels, fyEndMonth);
            const isOn = selection.consolidating_is || selection.consolidating_bs;
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 8, borderTop: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--theme-ink)" }}>Consolidating statements</div>
                <div style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
                  One sheet per selected period — each entity gets its own column, with a far-right total that ties back to Combined. Turning either checkbox on locks the "IS + BS" boxes above for all Combined-marked entities.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={{ display: "inline-flex", alignItems: "center", fontSize: 12, color: "#1f2937", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={selection.consolidating_is}
                      onChange={(e) => setSelection((prev) => ({ ...prev, consolidating_is: e.target.checked }))}
                      style={{ marginRight: 6 }}
                    />
                    Consolidating IS
                  </label>
                  <label style={{ display: "inline-flex", alignItems: "center", fontSize: 12, color: "#1f2937", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={selection.consolidating_bs}
                      onChange={(e) => setSelection((prev) => ({ ...prev, consolidating_bs: e.target.checked }))}
                      style={{ marginRight: 6 }}
                    />
                    Consolidating BS
                  </label>
                </div>
                {isOn && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "var(--theme-ink-soft)", marginBottom: 4 }}>Periods</div>
                    {fyOptions.length === 0 ? (
                      <div style={{ fontSize: 11, color: "#9a3412", fontStyle: "italic" }}>
                        No fiscal years detected from your period headers. Check your "First Period" column mapping.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {fyOptions.map((fy) => (
                          <label key={fy} style={{ display: "inline-flex", alignItems: "center", fontSize: 12, color: "#1f2937", cursor: "pointer", padding: "2px 8px", border: "1px solid var(--theme-border)", borderRadius: 4, background: consolidatingPeriods.includes(fy) ? "#eef2ff" : "white" }}>
                            <input
                              type="checkbox"
                              checked={consolidatingPeriods.includes(fy)}
                              onChange={(e) => setConsolidatingPeriods((prev) =>
                                e.target.checked ? [...prev, fy] : prev.filter((p) => p !== fy),
                              )}
                              style={{ marginRight: 4 }}
                            />
                            {fy}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* IS section classification — shown when analyze returned L1 candidates. */}
      {isSectionCandidates.length > 0 && (
        <div style={{ marginBottom: 16, padding: "12px 14px", background: "#f8f9fa", borderRadius: 6, border: "1px solid #e0e4e8" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#213D63", marginBottom: 10 }}>
            IS Section Classification
          </div>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
            Confirm which category each section belongs to. This drives Gross Profit and EBITDA.
          </div>
          {isSectionCandidates.map((c) => (
            <div key={c.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <div style={{ flex: 1, fontSize: 13, color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</div>
              <select
                value={isSectionMapping[c.label] || "OPEX"}
                onChange={(e) => setIsSectionMapping((prev) => ({ ...prev, [c.label]: e.target.value }))}
                style={{ fontSize: 12, padding: "3px 6px", border: "1px solid #ccc", borderRadius: 4, background: "white", color: "#333" }}
              >
                <option value="Revenue">Revenue</option>
                <option value="COGS">COGS</option>
                <option value="OPEX">OPEX</option>
                <option value="Below EBITDA">Below EBITDA</option>
              </select>
            </div>
          ))}
        </div>
      )}

      {/* Create Databook button — only after the mapping has been applied. */}
      {upload && analysis && (
        <button
          onClick={handleCreateDatabook}
          disabled={!buildAction || busy || !Object.values(selection).some(Boolean)}
          style={{
            padding: 12,
            borderRadius: 10,
            border: "none",
            background: !buildAction || busy || !Object.values(selection).some(Boolean) ? "#9ca3af" : "var(--theme-accent)",
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: !buildAction || busy || !Object.values(selection).some(Boolean) ? "not-allowed" : "pointer",
          }}
        >
          {phase === "building"
            ? "Building databook…"
            : phase === "analyzing"
              ? "Waiting for analysis…"
              : phase === "uploading"
                ? "Uploading…"
                : !buildAction && upload
                  ? "No buildable layout detected"
                  : !Object.values(selection).some(Boolean)
                    ? "Pick at least one sheet"
                    : "Create Databook"}
        </button>
      )}

      {/* Estimated AI cost — rough back-of-envelope, updates with selection. */}
      {upload && analysis && (analysis.isAccountCount > 0 || analysis.bsAccountCount > 0) && (() => {
        const { low, high } = estimateAICost(selection, analysis.isAccountCount, analysis.bsAccountCount);
        const anyAI =
          selection.clean_account_names ||
          selection.dso_dpo ||
          selection.bs_stratified ||
          selection.nwc ||
          selection.net_debt ||
          selection.is_trending ||
          selection.bs_trending;
        return (
          <div style={{ fontSize: 11, color: "#6b7280", textAlign: "center" }}>
            {anyAI
              ? `Estimated AI cost: ~${formatCost(low)} – ${formatCost(high)} (rough)`
              : "No AI features selected — no Claude usage."}
          </div>
        );
      })()}

      {/* Live build progress label — only visible while a job is running. */}
      {buildProgress && (
        <div style={{ fontSize: 12, color: "#213D63", textAlign: "center", fontWeight: 600 }}>
          {buildProgress}…
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: 10,
            color: "#dc2626",
            fontSize: 13,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      {/* Done */}
      {phase === "done" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div
            style={{
              background: "var(--theme-tint-9)",
              border: "1px solid var(--theme-accent)",
              borderRadius: 8,
              padding: 10,
              color: "var(--theme-action)",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Databook downloaded — check your Downloads folder.
          </div>
          {buildWarnings.length > 0 && (
            <div
              style={{
                background: "#fffbeb",
                border: "1px solid #f59e0b",
                borderRadius: 8,
                padding: 12,
                color: "#92400e",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                {buildWarnings.length} item{buildWarnings.length === 1 ? "" : "s"} you selected didn't produce output:
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {buildWarnings.map((w, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {glConflictState && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 8,
              padding: 24,
              width: 720,
              maxWidth: "92vw",
              maxHeight: "85vh",
              overflow: "auto",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "#213D63" }}>
              {glConflictState.conflicts.length} GL #{glConflictState.conflicts.length === 1 ? " has" : "s have"} inconsistent mappings
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.5 }}>
              The databook aggregates by GL #. When the same GL appears in your TB with different
              account names or mappings, the build's totals can come out wrong. Pick the version
              you want to canonicalize on — the other rows get rewritten to match. Updated cells
              are highlighted yellow on the Master TB tab of the output.
            </div>

            {glConflictState.conflicts.map((c) => (
              <div key={c.glKey} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                  GL # <span style={{ fontFamily: "monospace", background: "#f3f4f6", padding: "1px 6px", borderRadius: 3 }}>{c.glKey}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {c.fingerprints.map((fp, idx) => {
                    const selected = (glConflictState.picks[c.glKey] ?? 0) === idx;
                    return (
                      <label
                        key={idx}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                          padding: 8,
                          border: selected ? "1px solid #213D63" : "1px solid #e5e7eb",
                          borderRadius: 4,
                          background: selected ? "#eef2ff" : "white",
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                      >
                        <input
                          type="radio"
                          name={`gl-${c.glKey}`}
                          checked={selected}
                          onChange={() => setGlConflictState((prev) => prev && ({ ...prev, picks: { ...prev.picks, [c.glKey]: idx } }))}
                          style={{ marginTop: 2 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 6 }}>
                            {c.columns.map((col, ci) => (
                              <div key={ci}>
                                <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 600 }}>{col}</div>
                                <div style={{ fontSize: 12, color: "#1f2937" }}>{fp.values[ci] || <span style={{ color: "#9ca3af", fontStyle: "italic" }}>(blank)</span>}</div>
                              </div>
                            ))}
                          </div>
                          <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4 }}>
                            {fp.rowCount} row{fp.rowCount === 1 ? "" : "s"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => { setGlConflictState(null); setPhase("mapping"); }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // Default any GL without an explicit pick to fingerprint 0
                  // (the most common one — server sorted descending by row count).
                  const picks: Record<string, number> = {};
                  for (const c of glConflictState.conflicts) {
                    picks[c.glKey] = glConflictState.picks[c.glKey] ?? 0;
                  }
                  const mapping = glConflictState.mapping;
                  const rebuild = pendingRebuild;
                  setGlConflictState(null);
                  setPhase("analyzing");
                  if (rebuild && upload) {
                    setPendingRebuild(null);
                    void runRebuildAuto(upload.sessionId, rebuild, picks);
                  } else {
                    void runAnalyze(mapping, picks);
                  }
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "#213D63",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Apply &amp; continue
              </button>
            </div>
          </div>
        </div>
      )}

      {revenuePicker && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 8,
              padding: 24,
              width: 460,
              maxWidth: "92vw",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "#213D63" }}>
              Which mapping value is your revenue?
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.5 }}>
              We couldn't auto-detect a revenue / sales label in your mapping
              columns. Pick the one that represents revenue so we can run the
              cumulative-TB check.
            </div>
            <select
              value={revenuePicker.pick || ""}
              onChange={(e) =>
                setRevenuePicker((prev) => prev && ({ ...prev, pick: e.target.value || undefined }))
              }
              style={{
                width: "100%",
                padding: "6px 8px",
                fontSize: 13,
                border: "1px solid #ccc",
                borderRadius: 4,
                marginBottom: 16,
              }}
            >
              <option value="">— pick a label —</option>
              {revenuePicker.candidates.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.value} ({c.rowCount} rows)
                </option>
              ))}
            </select>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => { setRevenuePicker(null); setPhase("ready"); }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const pick = revenuePicker.pick;
                  if (!pick) { pushError("Pick a label before continuing."); return; }
                  setRevenuePicker(null);
                  void runBuild(undefined, undefined, pick);
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "#213D63",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Use this &amp; continue
              </button>
            </div>
          </div>
        </div>
      )}

      {cumulativePrompt && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 8,
              padding: 24,
              width: 640,
              maxWidth: "94vw",
              maxHeight: "88vh",
              overflow: "auto",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "#213D63" }}>
              Cumulative months detected
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.5 }}>
              We summed your revenue accounts and looked for stretches of
              consecutive months where the magnitude keeps growing — the
              fingerprint of a year-to-date TB. Pick which runs to
              decumulate. The transformed cells get highlighted yellow on
              the Master TB tab of the output.
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {cumulativePrompt.runs.map((r) => (
                <label
                  key={r.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!cumulativePrompt.picks[r.key]}
                    onChange={(e) =>
                      setCumulativePrompt((prev) =>
                        prev && ({ ...prev, picks: { ...prev.picks, [r.key]: e.target.checked } }),
                      )
                    }
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>
                      {r.periodLabels[0]} → {r.periodLabels[r.periodLabels.length - 1]}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {r.monthCount} consecutive months trending up
                      {r.includesTrailing ? " (includes the trailing partial period)" : ""}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 4 }}>
              Summed revenue per period
            </div>
            <div style={{ border: "1px solid #e5e7eb", borderRadius: 4, maxHeight: 240, overflow: "auto", marginBottom: 16 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead style={{ background: "#f9fafb", position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid #e5e7eb" }}>Period</th>
                    <th style={{ textAlign: "right", padding: "4px 8px", borderBottom: "1px solid #e5e7eb" }}>Revenue (sum)</th>
                    <th style={{ textAlign: "left", padding: "4px 8px", borderBottom: "1px solid #e5e7eb" }}>Marker</th>
                  </tr>
                </thead>
                <tbody>
                  {cumulativePrompt.table.map((r) => (
                    <tr key={r.periodIdx}>
                      <td style={{ padding: "3px 8px" }}>{r.periodLabel}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right", fontFamily: "monospace" }}>
                        {Number.isFinite(r.summedRevenue) ? r.summedRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 }) : ""}
                      </td>
                      <td style={{ padding: "3px 8px", color: r.marker === "cumulating" ? "#9a3412" : r.marker === "reset" ? "#16a34a" : "#6b7280" }}>
                        {r.marker === "cumulating" ? "↑ cumulating" : r.marker === "reset" ? "↓ reset" : "start"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => { setCumulativePrompt(null); setPhase("ready"); }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  // "Proceed without decumulating" → send an empty picks
                  // object so the server treats this as a deliberate
                  // decision and doesn't 422 again on the next call.
                  const rev = cumulativePrompt.revenueOverride;
                  setCumulativePrompt(null);
                  void runBuild(undefined, {}, rev);
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "white",
                  border: "1px solid #213D63",
                  color: "#213D63",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Proceed without decumulating
              </button>
              <button
                type="button"
                onClick={() => {
                  const picks = cumulativePrompt.picks;
                  const rev = cumulativePrompt.revenueOverride;
                  setCumulativePrompt(null);
                  void runBuild(undefined, picks, rev);
                }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "#213D63",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Decumulate &amp; continue
              </button>
            </div>
          </div>
        </div>
      )}

      {sectionPrompt && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setSectionPrompt(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "white",
              borderRadius: 8,
              padding: 24,
              width: 540,
              maxWidth: "90vw",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: "#213D63" }}>
              Couldn't identify some BS sections
            </div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.5 }}>
              The Net Working Capital and Net Debt schedules need to know which of your
              top-level Mapping-1 groups represents each canonical BS section. Pick the
              matching group below. Your pick is saved server-side so the next deal with
              the same label resolves automatically.
            </div>

            {sectionPrompt.missing.map((key) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  {SECTION_LABELS[key]}
                </div>
                <select
                  value={sectionPicks[key] || ""}
                  onChange={(e) =>
                    setSectionPicks((prev) => ({ ...prev, [key]: e.target.value || undefined }))
                  }
                  style={{
                    width: "100%",
                    padding: "6px 8px",
                    fontSize: 13,
                    border: "1px solid #ccc",
                    borderRadius: 4,
                  }}
                >
                  <option value="">— pick a top-level BS group —</option>
                  {sectionPrompt.candidates.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => { setSectionPrompt(null); setPhase("ready"); }}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "white",
                  border: "1px solid #ccc",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSectionPromptSubmit}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  background: "#213D63",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                }}
              >
                Continue build
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DatabookPanel;
